import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    viewChildren,
    WritableSignal
} from "@angular/core";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {AnalyticsService} from '../../logging/analytics.service';
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {OrderInitiationStatus, OrderModel, TransferRequest} from "../order.model";
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {OrderTransferTargetStatus} from '../../group-order/order-transfer-request-poll.service';
import {MenuModel} from '../../menu/menu.model';
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {ToggleComponent} from "../../ui-elements/toggle/toggle.component";
import {OrderExtrasPanelComponent} from "../order-extras-panel/order-extras-panel.component";
import {
    OrderItemQuantityControlComponent
} from "../order-item/order-item-quantity-control/order-item-quantity-control.component";
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {PossessivePipe} from '../../utils/possessive.pipe';
import {CurrencyPipe} from '../../utils/currency.pipe';
import {LinkComponent} from '../../buttons/link/link.component';
import {HtmlPipe} from '../../utils/html.pipe';
import {CssVarDirective} from '../../utils/css-var.directive';
import {DisplayableOrderItem, OrderType} from '../order';
import {GroupOrderModel} from '../../group-order/group-order.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-order-edit-panel",
    templateUrl: "order-edit-panel.component.html",
    styleUrl: "./order-edit-panel.component.scss",
    imports: [
        ContentMessageComponent,
        CssVarDirective,
        CurrencyPipe,
        HtmlPipe,
        IconComponent,
        ImageCacheDirective,
        LinkComponent,
        NgClass,
        OrderExtrasPanelComponent,
        OrderItemQuantityControlComponent,
        ToggleComponent,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class OrderEditPanelComponent {
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderModel: OrderModel = inject(OrderModel);
    public groupOrderModel: GroupOrderModel = inject(GroupOrderModel);
    public menuModel: MenuModel = inject(MenuModel);
    private analyticsService: AnalyticsService = inject(AnalyticsService);

    OrderType = OrderType;
    OrderInitiationStatus = OrderInitiationStatus;
    MessageLevel = MessageLevel;
    OrderTransferTargetStatus = OrderTransferTargetStatus;

    public readonly showRetry: InputSignal<boolean> = input<boolean>(true);
    public readonly showItemEdit: InputSignal<boolean> = input<boolean>(true);
    public readonly disabled: InputSignal<boolean> = input<boolean>(false);
    public readonly collapseTransferRequests: InputSignal<boolean> = input<boolean>(true);

    public onRetry: OutputEmitterRef<void> = output<void>();
    public onOrderItemRemoved: OutputEmitterRef<DisplayableOrderItem> = output<DisplayableOrderItem>();

    private requestItems: Signal<ReadonlyArray<ElementRef>> = viewChildren("requestItems");

    public readonly transferRequestHeights: WritableSignal<{ [transferRequestId: string]: number }> = signal<{
        [transferRequestId: string]: number
    }>({});

    public readonly showRequestHeadings: Signal<boolean> = computed<boolean>(() =>
        !!this.orderModel.totalTransfers() || this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN);

    public readonly transferRequestExpanded: WritableSignal<Map<string, boolean>> = signal(new Map());

    constructor() {
        effect(() => {
            if (this.requestItems().length === this.orderModel.transferRequests().length) {
                this.orderModel.transferRequests().forEach((request: TransferRequest) => this.setTransferRequestHeight(request))
            }
        });

        effect(() => this.transferRequestExpanded.update(map =>
            this.orderModel.transferRequests().reduce((newMap: Map<string, boolean>, request: TransferRequest) =>
                newMap.set(request.transferRequestId, map.get(request.transferRequestId) ?? (!request.transferRequestId || !this.collapseTransferRequests())), new Map())));
    }

    public async removeOrderItem(orderItem: DisplayableOrderItem): Promise<void> {
        if (await this.orderModel.removeOrderItem(orderItem)) {
            this.onOrderItemRemoved.emit(orderItem);
        }
    }

    public toggleTransferRequest(request: TransferRequest): void {
        this.transferRequestExpanded.update(map => new Map(map).set(request.transferRequestId, !map.get(request.transferRequestId)));
        this.setTransferRequestHeight(request);
    }

    public setTransferRequestHeight(request: TransferRequest): void {
        if (!this.showRequestHeadings || !this.requestItems().length) {
            return;
        }
        const requestIndex: number = this.orderModel.transferRequests().indexOf(request);
        const items: HTMLElement = this.requestItems()[requestIndex].nativeElement;
        setTimeout(() => {
            this.transferRequestHeights.set({
                ...this.transferRequestHeights(),
                [request.transferRequestId]: this.transferRequestExpanded().get(request.transferRequestId) ? items.scrollHeight : 0
            });
            if (this.transferRequestExpanded().get(request.transferRequestId)) {
                setTimeout(() => items.scrollIntoView({
                    block: "end",
                    inline: "nearest",
                    behavior: "smooth"
                }), 330);
            }
        });
    }

    public getTransferRequestName(request: TransferRequest) {
        return request.transferRequestId ? new PossessivePipe().transform(request.transferRequestName) : undefined;
    }

    public getTransferRequestHeading(request: TransferRequest) {
        return request.transferRequestId ? "woa.heading.order.summary.transferred" : "woa.heading.order.summary.local";
    }

    public async quantityChanged(orderItem: DisplayableOrderItem): Promise<void> {
        return this.analyticsService.trackItemQtyChanged(orderItem);
    }
}
